import { ArrowDown, ArrowUp } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Empty } from '@creditornot/cb-placeholders';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, LinkProps } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';

import { InfiniteList } from 'common/components';
import Sorting from 'common/components/icons/Sorting';
import { useIsMobile } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import typographyCss from 'common/typography/Typography/typographyCss';

import { SortOrder, SortState } from './types';

type Column<RecordT> = {
  id: string;
  label: string;
  width: number;
  enableSort?: boolean;
  handleSort?: (sortOrder: SortOrder) => void;
  render: (record: RecordT) => React.ReactNode;
};

type InfiniteTableProps<RecordT> = {
  className?: string;
  fetchingNextPage: boolean;
  fetchingPreviousPage: boolean;
  selected?: RecordT;
  items: RecordT[];
  columns: Column<RecordT>[];
  sort?: SortState<Column<RecordT>['id']>;
  visibleRowsCount?: number;
  onFetchNextPage?: () => void;
  onFetchPreviousPage?: () => void;
  onSelect?: (record: RecordT) => void;
  link?: (record: RecordT) => LinkProps['to'];
};

const ROW_HEIGHT = 52;

// Table styles
const TableRowBase = styled.div`
  display: flex;
  box-sizing: border-box;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding: 18px 16px;
  color: inherit;
`;

const TableRow = styled(TableRowBase)<{ isMobile: boolean; isLast?: boolean; selected?: boolean; hoverable?: boolean }>`
  border-block-end: ${({ isLast }) => (isLast ? 0 : '1px')} solid ${cbSemanticCssVars.borderPrimary};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '8px' : '0')};
  padding: 16px;
  ${({ hoverable }) => {
    return (
      hoverable &&
      css`
        cursor: pointer;
        &:hover {
          background-color: ${cssThemedValue({ light: colors.pepper4, dark: colors.salt4 })};
        }
      `
    );
  }};

  ${({ selected }) => {
    return (
      selected &&
      css`
        background-color: ${colors.wolt8};
      `
    );
  }};
`;

const Cell = styled.div`
  align-items: center;
  display: flex;
  text-align: start;
`;

const TableHeaderCell = styled(Cell)<{ widthPercentage: number }>`
  ${typographyCss.Body3}
  font-weight: 600;
  color: ${cbSemanticCssVars.textPrimary};
  display: flex;
  gap: 8px;
  width: ${({ widthPercentage }) => widthPercentage}%;
  padding-inline-end: 16px;
`;

const TableRowCell = styled(Cell)<{ widthPercentage: number; isMobile: boolean }>`
  ${typographyCss.Body3}
  width: ${({ widthPercentage, isMobile }) => (isMobile ? 100 : widthPercentage)}%;
  justify-content: space-between;
  padding-inline-end: ${({ isMobile }) => (isMobile ? '0' : '16px')};
  gap: ${({ isMobile }) => (isMobile ? '16px' : '0')};

  min-width: 0;
  white-space: nowrap;
  flex-wrap: nowrap;
`;

const TableRowValue = styled(Cell)<{ isMobile: boolean }>`
  justify-content: flex-start;
  text-align: ${({ isMobile }) => (isMobile ? 'end' : 'start')};
  width: ${({ isMobile }) => (isMobile ? 'auto' : '100%')};

  min-width: 0;
  white-space: nowrap;
  flex-wrap: nowrap;
`;

// Component styles
const InfiniteTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow-y: auto;
`;

const InfiniteListWrapper = styled.div<{ height?: number }>`
  flex: 1 1 auto;
  overflow: hidden;
  ${({ height }) => (height ? `height: ${height}px;` : '')}
`;

const EmptyMessage = styled(Empty)`
  margin: 48px 16px;
`;

const SortingIcon = styled(Sorting)`
  fill: ${cbSemanticCssVars.textSecondary};
  width: 12px;
  cursor: pointer;
`;

const ArrowDownIcon = styled(ArrowDown)`
  fill: ${cbSemanticCssVars.textSecondary};
  width: 12px;
  cursor: pointer;
`;

const ArrowUpIcon = styled(ArrowUp)`
  fill: ${cbSemanticCssVars.textSecondary};
  width: 12px;
  cursor: pointer;
`;

const getTableRowHeight = (isMobile: boolean, columns: unknown[]) => {
  const LINE_HEIGHT = 20;
  const GAP = 8;
  const PADDING = 20 * 2;
  const BORDER = 1;

  return isMobile ? (LINE_HEIGHT + GAP) * columns.length - GAP + PADDING + BORDER : ROW_HEIGHT;
};

const InfiniteTable = <RecordT extends { id: string }>({
  className,
  items,
  columns,
  fetchingNextPage,
  fetchingPreviousPage,
  sort,
  visibleRowsCount,
  onFetchNextPage,
  onFetchPreviousPage,
  selected,
  onSelect,
  link,
}: InfiniteTableProps<RecordT>) => {
  const isEmpty = items.length === 0 && !onFetchNextPage && !onFetchPreviousPage;
  const theme = useTheme();
  const isMobile = useIsMobile();
  const intl = useIntl();
  const [sorting, setSorting] = useState<SortState<Column<RecordT>['id']> | undefined>(sort);
  const hoverable = !!onSelect || !!link;
  const rowHeight = getTableRowHeight(isMobile, columns);

  const handleSorting = (column: Column<RecordT>, sortOrder: SortOrder) => {
    setSorting({ sortBy: column.id, sortOrder });
    column.handleSort?.(sortOrder);
  };

  const TableRowComponent: React.ComponentType<React.ComponentProps<typeof TableRow> & Partial<Pick<LinkProps, 'to'>>> =
    link
      ? styled(TableRow).attrs({
          as: Link,
        })``
      : TableRow;

  return (
    <InfiniteTableContainer className={className}>
      {!isMobile && (
        <TableRowBase>
          {columns.map(column => (
            <TableHeaderCell key={column.id} widthPercentage={column.width}>
              <div>{column.label}</div>
              {column.enableSort && (
                <>
                  {sorting?.sortBy !== column.id && (
                    <SortingIcon onClick={() => handleSorting(column, SortOrder.ASC)} />
                  )}
                  {sorting?.sortBy === column.id && (
                    <>
                      {sorting?.sortOrder === SortOrder.DESC && (
                        <ArrowDownIcon onClick={() => handleSorting(column, SortOrder.ASC)} />
                      )}
                      {sorting?.sortOrder === SortOrder.ASC && (
                        <ArrowUpIcon onClick={() => handleSorting(column, SortOrder.DESC)} />
                      )}
                    </>
                  )}
                </>
              )}
            </TableHeaderCell>
          ))}
        </TableRowBase>
      )}
      <InfiniteListWrapper height={visibleRowsCount ? Math.min(items.length, visibleRowsCount) * rowHeight : undefined}>
        {isEmpty ? (
          <EmptyMessage title={intl.formatMessage({ id: 'insights.no-records' })} />
        ) : (
          <InfiniteList
            fetchingNextPage={fetchingNextPage}
            fetchingPreviousPage={fetchingPreviousPage}
            onFetchNextPage={onFetchNextPage}
            onFetchPreviousPage={onFetchPreviousPage}
            itemSize={rowHeight}
            items={items}
            key={`${sort?.sortBy}-${sort?.sortOrder}`}
          >
            {({ item, style }: { item: RecordT; style: React.CSSProperties }) => (
              <TableRowComponent
                style={style}
                isMobile={isMobile}
                dir={theme.cbDirection}
                isLast={items.indexOf(item) === items.length - 1}
                hoverable={hoverable}
                selected={selected && selected.id === item.id}
                onClick={onSelect ? () => onSelect(item) : undefined}
                to={link ? link(item) : undefined}
              >
                {columns.map(column => (
                  <TableRowCell key={column.id} widthPercentage={column.width} isMobile={isMobile}>
                    {isMobile && <Typography.Title2>{column.label}</Typography.Title2>}
                    <TableRowValue isMobile={isMobile}>{column.render(item)}</TableRowValue>
                  </TableRowCell>
                ))}
              </TableRowComponent>
            )}
          </InfiniteList>
        )}
      </InfiniteListWrapper>
    </InfiniteTableContainer>
  );
};
export default InfiniteTable;
